import { create } from 'zustand';
import {
  collection,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { getPreviousShifts, type RemoteShiftRow } from '@/services/api/get-shifts';
import { CURRENT_SHIFT_ID } from '@/models/shift';

export type ShiftRow = {
  shiftId: string;
  dateStarted: string;
  amount: string;
  isPending: boolean;
};

async function loadPendingShifts(uid: string): Promise<ShiftRow[]> {
  const q = query(
    collection(db, 'users', uid, 'shifts'),
    orderBy('dateStarted', 'desc'),
  );
  const snap = await getDocs(q);
  const rows: ShiftRow[] = [];
  for (const d of snap.docs) {
    if (d.id === CURRENT_SHIFT_ID) continue;
    const data = d.data();
    const dateStarted = String(data.dateStarted ?? '');
    if (!dateStarted || data.reportUrl) continue;
    rows.push({
      shiftId: d.id,
      dateStarted,
      amount: String(data.totalBilled ?? '0'),
      isPending: true,
    });
  }
  return rows;
}

function toShiftRow(r: RemoteShiftRow): ShiftRow {
  return {
    shiftId: String(r.shiftId ?? ''),
    dateStarted: String(r.dateStarted ?? r.date ?? ''),
    amount: String(r.amount ?? '0'),
    isPending: false,
  };
}

// Prefer completed (isPending=false) when both versions exist.
// WHY: backend /getShifts and Firestore can both contain the same shift
// transiently after submission.
function dedupAndSort(rows: ShiftRow[]): ShiftRow[] {
  const byId = new Map<string, ShiftRow>();
  for (const r of rows) {
    if (!r.shiftId) continue;
    const existing = byId.get(r.shiftId);
    if (!existing || (existing.isPending && !r.isPending)) byId.set(r.shiftId, r);
  }
  return [...byId.values()].sort((a, b) =>
    b.dateStarted.localeCompare(a.dateStarted),
  );
}

type HistoryState = {
  rows: ShiftRow[];
  isLoading: boolean;
  refresh: (uid: string) => Promise<void>;
  removeLocal: (shiftId: string) => void;
};

export const useHistoryStore = create<HistoryState>((set) => ({
  rows: [],
  isLoading: false,
  refresh: async (uid) => {
    set({ isLoading: true });
    const [completed, pending] = await Promise.all([
      getPreviousShifts(uid).catch(() => [] as RemoteShiftRow[]),
      loadPendingShifts(uid).catch(() => [] as ShiftRow[]),
    ]);
    const rows = dedupAndSort([...completed.map(toShiftRow), ...pending]);
    set({ rows, isLoading: false });
  },
  removeLocal: (shiftId) =>
    set((s) => ({ rows: s.rows.filter((r) => r.shiftId !== shiftId) })),
}));
